import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRouteMatch, withRouter } from "react-router-dom";
import { useStateValue } from "../../../context/store";
import {
  Grid,
  FormControl,
  Backdrop,
  CircularProgress,
  TextField,
  Tooltip,
  IconButton,
  Button,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import EditIcon from '@material-ui/icons/Edit';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { styles } from "./styles";
import { ERROR_MESSAGE, REQUIRED_MESSAGE } from "../../../constants";
import { defaultValuesFormat } from "./models";
import ModalConfirmBase from "../../../utils/form/modalConfirmBase";
import { updateContractTypeFormat } from "../../../actions/contract-type-actions";

const ContractModelsForm = ({ data, history, setRefresh, refresh }) => {

  const [{ }, dispatch] = useStateValue();
  const [showEditButtons, setShowEditButtons] = useState(false);
  const { path } = useRouteMatch();
  const classes = styles();
  const [isLoading, setIsLoading] = useState(false);
  const [confirm, setConfirm] = useState({
    open: false,
    question: "",
    oneButtons: false,
    handleClickOut: () => { },
    handleClick: () => { },
    type: "",
    title: "",
  });

  const schema = yup.object().shape({
    name: yup
    .string()
    .required(REQUIRED_MESSAGE)
    .min(5, "Ingrese mínimo 5 caracteres")
    .max(100, "Ingrese máximo 100 caracteres"),
    status: yup
      .string()
      .typeError("Este campo es obligatorio")
      .required(REQUIRED_MESSAGE)
    ,
  });

  const { register, handleSubmit, setValue, control, errors, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValuesFormat,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    setValue("name", data.name);
    setValue("status", data.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoToConfiguration = () => {
    history.push(`${path}/configuration/${data.id}/${data.name}`);
  };

  const handleClickClose = () => {
    setConfirm({...confirm, open:false});
  }

  const handleClickCloseSuccess = () => {
    setConfirm({...confirm, open:false});
  }

  const onSubmit = (info) => {
    const newData = {
      id: data.id,
      contractTypeId: data.contractType.id,
      name: info.name,
      status: info.status
    }
    setConfirm({
      question: "¿Está seguro que desea editar este formato? ",
      textButtonSubmit: "Guardar",
      handleClick: () => handleClick(newData),
      handleClickOut: () => handleClickClose(),
      type: "QUESTION",
      open: true,
    });
  };

  const handleClick = async (info) => {
    setConfirm({...confirm, open:false});
    const response = await updateContractTypeFormat(info);
    if(response?.status === 200 || response?.status === 201){
      setRefresh(!refresh);
      setShowEditButtons(false);
      setConfirm({
        open: true,
        question: `Formato actualizado exitosamente`,
        type: "SUCCESS",
        handleClick: () => handleClickCloseSuccess(),
        oneButtons: true,
      });
    }else if(response?.status > 399){
      setConfirm({
        open: true,
        question: response.data ? response?.data.detail : `El formato no a sido actualizado, intentalo nuevamente `,
        type: "WARNING",
        handleClick: () => handleClickClose(),
        oneButtons: true,
      });
    }else{
      setConfirm({
        open: true,
        question: `Ocurrió un error procesando la solicitud, inténtelo nuevamente`,
        type: "WARNING",
        handleClick: () => handleClickClose(),
        oneButtons: true,
      });
    }
  }

  const cancelEditFormat = () => {
    reset({
      name: data.name,
      status: data.status
    });
    setShowEditButtons(false);
  }

  return (
    <React.Fragment>
      <ModalConfirmBase
        description={confirm.question}
        open={confirm.open}
        handleClick={confirm.handleClick}
        handleClickOut={confirm.handleClickOut}
        type={confirm.type}
        oneButtons={confirm.oneButtons}
        title={confirm.title}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classes.form}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <FormControl fullWidth>
              <TextField
                fullWidth
                variant="outlined"
                inputRef={register}
                className={classes.dropdown}
                inputProps={{
                  style: {
                    height: "18px",
                    borderRadius: "8px",
                    paddingBottom: "6.5px",
                    fontSize: 14,
                  },
                }}
                label="Nombre del formato tipo de contrato"
                name="name"
                size="small"
                disabled={!showEditButtons}
                InputLabelProps={{ shrink: true }}
                error={
                  !!errors.hasOwnProperty("name") &&
                  errors["name"].message
                }
                helperText={
                  errors.hasOwnProperty("name") &&
                  errors["name"].message
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <FormControl
              variant="outlined"
              fullWidth
              size="small"
              error={
                !!errors.hasOwnProperty("status") && errors["status"].message
              }
            >
              <InputLabel
                htmlFor="outlined-select-status"
                shrink={true}
                size="small"
              >
                Estado
              </InputLabel>
              <Controller
                fullWidth
                control={control}
                inputRef={register}
                className={classes.dropdown}
                label="Estado"
                size="small"
                name="status"
                notched={true}
                disabled={!showEditButtons}
                defaultValue={null}
                inputProps={{
                  name: "status",
                  id: "outlined-select-status",
                }}
                as={
                  <Select>

                    <MenuItem
                      className={classes.menuItem}
                      aria-label="None"
                      value={null}
                    >
                      <em>&nbsp;</em>
                    </MenuItem>
                    <MenuItem value="Activo">Activo</MenuItem>
                    <MenuItem value="Inactivo">Inactivo</MenuItem>
                  </Select>
                }
              />
              <FormHelperText>
                {errors.hasOwnProperty("status") && errors["status"].message}
              </FormHelperText>
            </FormControl>
          </Grid>
          {showEditButtons && (
            <>
              <Grid item xs={12} md={6} lg={1}>

                <Button
                  onClick={() => cancelEditFormat()}
                  color="primary"
                  variant="outlined"
                  style={{ color: "#7EBDCE", borderColor: "#E1E1E1" }}
                >
                  Cancelar
                </Button>
              </Grid>
              <div style={{ width: "20px" }} />
              <Grid item xs={12} md={6} lg={1}>
                <Button
                  type="submit"
                  color="primary"
                  autoFocus
                  style={{ backgroundColor: "#7EBDCE", color: "#ffffff" }}
                >
                  Editar
                </Button>
              </Grid></>
          )}

          {!showEditButtons && (
            <div
              className={classes.columnEvent}
              onClick={() => setShowEditButtons(!showEditButtons)}
            >
              <Tooltip title="Editar" arrow placement="top" style={{ marginTop: "7px" }}>
                <IconButton>
                  <EditIcon
                    style={{ fontSize: 20, pointerEvents: "auto" }}
                    className={classes.iconEvent}
                  />
                </IconButton>
              </Tooltip>
            </div>
          )}
          <div
            className={classes.columnEvent}
            onClick={handleGoToConfiguration}
          >
            <Tooltip title="Configurar" arrow placement="top" style={{ marginTop: "7px" }}>
              <IconButton>
                <SettingsIcon
                  style={{ fontSize: 20, pointerEvents: "auto" }}
                  className={classes.iconEvent}
                />
              </IconButton>
            </Tooltip>
          </div>
        </Grid>
      </form>



      <Backdrop
        //className={classes.backdrop}
        style={{ zIndex: "9999" }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </React.Fragment>
  );
};

export default withRouter(ContractModelsForm);
